import { ApiError, PaginatedApiResponse } from "@/lib/apiClient";
import * as orderService from "@/services/orderService";
import {
  Order,
  FETCH_ORDERS_REQUEST,
  FETCH_ORDERS_SUCCESS,
  FETCH_ORDERS_FAILURE,
  SEARCH_ORDERS_REQUEST,
  SEARCH_ORDERS_SUCCESS,
  SEARCH_ORDERS_FAILURE,
  FETCH_ORDER_DETAILS_REQUEST,
  FETCH_ORDER_DETAILS_SUCCESS,
  FETCH_ORDER_DETAILS_FAILURE,
  APPROVE_ORDER_REQUEST,
  APPROVE_ORDER_SUCCESS,
  APPROVE_ORDER_FAILURE,
  REJECT_ORDER_REQUEST,
  REJECT_ORDER_SUCCESS,
  REJECT_ORDER_FAILURE,
  FetchOrdersOptions,
  SearchOrdersOptions,
} from "@/types/models/order";
import { call, put, takeLatest } from "redux-saga/effects";
import { toast } from "sonner";

const getOrderErrorMessage = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message : fallback;

function* fetchOrdersSaga(action: {
  type: string;
  payload: FetchOrdersOptions;
}) {
  try {
    console.log('[OrderSaga] fetchOrdersSaga called with options:', action.payload);
    console.trace('[OrderSaga] Stack trace for fetch orders saga');
    
    const response: PaginatedApiResponse<Order[]> = yield call(
      orderService.getOrders,
      action.payload,
    );

    console.log('[OrderSaga] Orders fetched successfully, count:', response.data.length);

    yield put({
      type: FETCH_ORDERS_SUCCESS,
      payload: {
        list: response.data,
        pagination: response.pagination,
      },
    });
  } catch (error) {
    yield put({
      type: FETCH_ORDERS_FAILURE,
      payload:
        error instanceof ApiError ? error.message : "Failed to fetch orders",
    });
  }
}

function* searchOrdersSaga(action: {
  type: string;
  payload: SearchOrdersOptions;
}) {
  try {
    console.log('[OrderSaga] searchOrdersSaga called with options:', action.payload);
    console.trace('[OrderSaga] Stack trace for search orders saga');
    
    const response: PaginatedApiResponse<Order[]> = yield call(
      orderService.searchOrders,
      action.payload,
    );

    console.log('[OrderSaga] Search orders completed, count:', response.data.length);

    yield put({
      type: SEARCH_ORDERS_SUCCESS,
      payload: {
        list: response.data,
        pagination: response.pagination,
      },
    });
  } catch (error) {
    yield put({
      type: SEARCH_ORDERS_FAILURE,
      payload:
        error instanceof ApiError ? error.message : "Failed to search orders",
    });
  }
}

function* fetchOrderDetailsSaga(action: { type: string; payload: string }) {
  try {
    console.log('[OrderSaga] fetchOrderDetailsSaga called with orderId:', action.payload);
    console.trace('[OrderSaga] Stack trace for order details saga');
    
    const response: { data: Order } = yield call(
      orderService.getOrderDetails,
      action.payload,
    );

    console.log('[OrderSaga] Order details fetched successfully:', response.data.id);

    yield put({
      type: FETCH_ORDER_DETAILS_SUCCESS,
      payload: response.data,
    });
  } catch (error) {
    yield put({
      type: FETCH_ORDER_DETAILS_FAILURE,
      payload:
        error instanceof ApiError
          ? error.message
          : "Failed to fetch order details",
    });
  }
}

function* approveOrderSaga(action: { type: string; payload: string }) {
  try {
    yield call(orderService.approveOrder, action.payload);
    toast.success("Order accepted. All order sets were marked verified.");
    yield put({
      type: APPROVE_ORDER_SUCCESS,
      payload: action.payload,
    });
  } catch (error) {
    console.error("Saga error in approveOrderSaga:", error);
    const errorMessage = getOrderErrorMessage(error, "Failed to approve order");
    toast.error(errorMessage);
    yield put({
      type: APPROVE_ORDER_FAILURE,
      payload: {
        orderId: action.payload,
        error: errorMessage,
      },
    });
  }
}

function* rejectOrderSaga(action: { type: string; payload: string }) {
  try {
    yield call(orderService.rejectOrder, action.payload);
    toast.success("Order cancelled. All order sets were cancelled.");
    yield put({
      type: REJECT_ORDER_SUCCESS,
      payload: action.payload,
    });
  } catch (error) {
    const errorMessage = getOrderErrorMessage(error, "Failed to reject order");
    toast.error(errorMessage);
    yield put({
      type: REJECT_ORDER_FAILURE,
      payload: {
        orderId: action.payload,
        error: errorMessage,
      },
    });
  }
}

export function* orderSaga() {
  yield takeLatest(FETCH_ORDERS_REQUEST, fetchOrdersSaga);
  yield takeLatest(SEARCH_ORDERS_REQUEST, searchOrdersSaga);
  yield takeLatest(FETCH_ORDER_DETAILS_REQUEST, fetchOrderDetailsSaga);
  yield takeLatest(APPROVE_ORDER_REQUEST, approveOrderSaga);
  yield takeLatest(REJECT_ORDER_REQUEST, rejectOrderSaga);
}
